import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Receipt, Wallet, AlertCircle, FileDown, BellRing, Download } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import api, { downloadUrl, formatApiError } from "../../lib/api";

const OVERDUE_DAYS = 14;
const isOverdue = (inv) => inv.status !== "paid" && (Date.now() - new Date(inv.created_at).getTime()) / 86400000 > OVERDUE_DAYS;
const daysOut = (inv) => Math.floor((Date.now() - new Date(inv.created_at).getTime()) / 86400000);

export default function AdminBilling() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("all");
  const [month, setMonth] = useState("all");
  const [reminding, setReminding] = useState("");

  useEffect(() => {
    api.get("/admin/invoices").then((r) => setData(r.data)).catch(() => setData({ invoices: [], totals: {} }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only fetch; api is a stable module import
  }, []);

  const months = useMemo(() => {
    const set = new Set((data?.invoices || []).map((i) => i.created_at.slice(0, 7)));
    return [...set].sort().reverse();
  }, [data]);

  const filtered = useMemo(() => (data?.invoices || []).filter((i) =>
    (status === "all" || (status === "overdue" ? isOverdue(i) : i.status === status)) &&
    (month === "all" || i.created_at.slice(0, 7) === month)
  ), [data, status, month]);

  const totals = useMemo(() => {
    const t = {};
    for (const inv of filtered) {
      const c = t[inv.currency] || { paid: 0, outstanding: 0, paid_count: 0, unpaid_count: 0 };
      if (inv.status === "paid") { c.paid += inv.amount; c.paid_count += 1; }
      else { c.outstanding += inv.amount; c.unpaid_count += 1; }
      t[inv.currency] = c;
    }
    return Object.entries(t);
  }, [filtered]);

  const overdueCount = useMemo(() => filtered.filter(isOverdue).length, [filtered]);

  const remind = async (inv) => {
    setReminding(inv.id);
    try {
      await api.post(`/admin/invoices/${inv.id}/remind`);
      toast.success(`Reminder sent to ${inv.client_name || "the doctor"}`);
      const r = await api.get("/admin/invoices");
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setReminding(""); }
  };

  const exportCsv = () => {
    const head = ["Case", "Doctor", "Clinic", "Amount", "Currency", "Status", "Method", "Payment Ref", "Issued", "Paid", "Last Reminder"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = filtered.map((i) => [i.case_ref, i.client_name, i.clinic_name, Number(i.amount).toFixed(2), i.currency,
      i.status, i.payment_method, i.payment_ref, i.created_at, i.paid_at, i.last_reminder_at].map(esc).join(","));
    const blob = new Blob([[head.map(esc).join(","), ...rows].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `orthoplanix-invoices-${month === "all" ? "all" : month}${status === "all" ? "" : "-" + status}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    toast.success(`Exported ${filtered.length} invoice(s) to CSV`);
  };

  if (data === null) {
    return <div className="flex justify-center py-24"><div className="h-8 w-8 rounded-full border-2 border-sky-600 border-t-transparent animate-spin" /></div>;
  }

  return (
    <div data-testid="admin-billing-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display font-black text-3xl tracking-tight mb-2">Billing & Revenue</h1>
          <p className="text-sm text-slate-500">Every case invoice, paid and outstanding, in one place.</p>
        </div>
        <Button onClick={exportCsv} disabled={filtered.length === 0} variant="outline" data-testid="billing-export-csv-btn"
          className="rounded-full font-bold border-slate-300">
          <Download className="h-4 w-4 mr-2" /> Export CSV ({filtered.length})
        </Button>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44 h-11 bg-white" data-testid="billing-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="unpaid">Unpaid</SelectItem>
            <SelectItem value="overdue">Overdue (&gt;{OVERDUE_DAYS}d)</SelectItem>
          </SelectContent>
        </Select>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="w-48 h-11 bg-white" data-testid="billing-month-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All months</SelectItem>
            {months.map((m) => (
              <SelectItem key={m} value={m}>
                {new Date(m + "-02").toLocaleDateString(undefined, { month: "long", year: "numeric" })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {totals.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {totals.map(([cur, t]) => (
            <div key={cur} className="contents">
              <div className="bg-white border border-emerald-200 rounded-xl p-5" data-testid={`revenue-paid-${cur}`}>
                <Wallet className="h-5 w-5 text-emerald-600 mb-3" />
                <p className="font-display font-black text-3xl">{cur} {t.paid.toFixed(2)}</p>
                <p className="text-xs text-slate-500 mt-1 uppercase tracking-wider">Collected · {t.paid_count} invoice(s)</p>
              </div>
              <div className="bg-white border border-amber-200 rounded-xl p-5" data-testid={`revenue-outstanding-${cur}`}>
                <AlertCircle className="h-5 w-5 text-amber-500 mb-3" />
                <p className="font-display font-black text-3xl">{cur} {t.outstanding.toFixed(2)}</p>
                <p className="text-xs text-slate-500 mt-1 uppercase tracking-wider">
                  Outstanding · {t.unpaid_count} invoice(s)
                  {overdueCount > 0 && <span className="text-red-500 font-bold"> · {overdueCount} overdue</span>}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl py-20 text-center" data-testid="invoices-empty-state">
          <Receipt className="h-10 w-10 text-slate-300 mx-auto mb-4" />
          <p className="font-display font-bold text-xl">No invoices match</p>
          <p className="text-sm text-slate-500 mt-2">Adjust the filters, or create an invoice from any case's Billing panel.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="invoices-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3.5 font-semibold">Case</th>
                  <th className="px-5 py-3.5 font-semibold">Doctor</th>
                  <th className="px-5 py-3.5 font-semibold">Amount</th>
                  <th className="px-5 py-3.5 font-semibold">Status</th>
                  <th className="px-5 py-3.5 font-semibold">Method</th>
                  <th className="px-5 py-3.5 font-semibold">Issued</th>
                  <th className="px-5 py-3.5 font-semibold">Paid</th>
                  <th className="px-5 py-3.5" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((inv) => {
                  const overdue = isOverdue(inv);
                  return (
                  <tr key={inv.id} className={`border-b border-slate-100 last:border-0 transition-colors ${overdue ? "bg-red-50/50 hover:bg-red-50" : "hover:bg-slate-50/60"}`} data-testid={`invoice-row-${inv.case_ref}`}>
                    <td className="px-5 py-4">
                      <Link to={`/admin/cases/${inv.case_id}`} className="font-mono font-semibold text-sky-700 hover:underline inline-flex items-center gap-1" data-testid={`invoice-case-link-${inv.case_ref}`}>
                        {inv.case_ref} <ArrowRight className="h-3 w-3" />
                      </Link>
                    </td>
                    <td className="px-5 py-4">{inv.client_name || "—"}<span className="text-slate-400 text-xs"> {inv.clinic_name ? `· ${inv.clinic_name}` : ""}</span></td>
                    <td className="px-5 py-4 font-bold">{inv.currency} {Number(inv.amount).toFixed(2)}</td>
                    <td className="px-5 py-4">
                      {inv.status === "paid" ? (
                        <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border-emerald-200">paid</span>
                      ) : overdue ? (
                        <span className="inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider bg-red-50 text-red-600 border-red-200" data-testid={`invoice-overdue-${inv.case_ref}`}>
                          <AlertCircle className="h-3 w-3" /> overdue · {daysOut(inv)}d
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider bg-amber-50 text-amber-700 border-amber-200">unpaid</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-slate-500 text-xs">{inv.payment_method || "—"}</td>
                    <td className="px-5 py-4 text-slate-500 text-xs">{new Date(inv.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-4 text-slate-500 text-xs">{inv.paid_at ? new Date(inv.paid_at).toLocaleDateString() : "—"}</td>
                    <td className="px-5 py-4 text-right">
                      {inv.status === "paid" ? (
                        <button onClick={() => downloadUrl(`/invoices/${inv.id}/receipt`, `receipt-${inv.case_ref}.pdf`).catch((e) => toast.error(formatApiError(e)))}
                          data-testid={`invoice-receipt-${inv.case_ref}`}
                          className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600 hover:text-emerald-800">
                          <FileDown className="h-3.5 w-3.5" /> Receipt
                        </button>
                      ) : (
                        <button onClick={() => remind(inv)} disabled={reminding === inv.id}
                          data-testid={`invoice-remind-${inv.case_ref}`}
                          title={inv.last_reminder_at ? `Last reminded ${new Date(inv.last_reminder_at).toLocaleString()}` : "No reminder sent yet"}
                          className="inline-flex items-center gap-1 text-xs font-bold text-amber-600 hover:text-amber-800 disabled:opacity-50">
                          <BellRing className="h-3.5 w-3.5" /> {reminding === inv.id ? "Sending…" : "Remind"}
                        </button>
                      )}
                    </td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
